import {
  Box,
  Button,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import axios from "axios";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AdminAnalytics.css";
import { BASE_API_URL } from "../../../utils/constants";
import { IgniteAdmins } from "../../../FunctionalComponents/IgniteTalks/IgniteAdmins";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);
const AdminAnalyticsContent = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthView, setMonthView] = useState([]);
  const [open, setOpen] = useState(null);
  const [selectedBarData, setSelectedBarData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [monthdata, setmonthdata] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const [topPages, setTopPages] = useState([]);
  const [topVideos, setTopVideos] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAnalytics = async () => {
      try {
        const response = await axios.post(
          `${BASE_API_URL}/yearanalytics`,
          { year: selectedYear },
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          },
        );

        setMonthView(response.data);
        setAnalyticsError(false);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setAnalyticsError(true);
        }
      }
    };

    fetchAnalytics();

    return () => controller.abort();
  }, [selectedYear]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchDetails = async () => {
      try {
        const response = await axios.post(
          `${BASE_API_URL}/fetchDetailsPerMonth`,
          {
            month: selectedMonth,
            year: selectedYear,
            page,
            rowsPerPage,
          },
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
            signal: controller.signal,
          },
        );

        setmonthdata(response.data[0].Data);
        setTotalRows(response.data[0].totalRows);
        setAnalyticsError(false);
      } catch (error) {
        if (error.name !== "CanceledError") {
          setAnalyticsError(true);
        }
      }
    };

    fetchDetails();

    return () => controller.abort();
  }, [selectedMonth, selectedYear, page, rowsPerPage]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTopData = async () => {
      try {
        const [pagesRes, videosRes] = await Promise.all([
          axios.get(`${BASE_API_URL}/top-pages?year=${selectedYear}`, {
            headers: { Authorization: localStorage.getItem("token") },
            signal: controller.signal,
          }),
          axios.get(`${BASE_API_URL}/top-videos?year=${selectedYear}`, {
            headers: { Authorization: localStorage.getItem("token") },
            signal: controller.signal,
          }),
        ]);

        setTopPages(pagesRes.data || []);
        setTopVideos(videosRes.data || []);
      } catch (err) {
        if (err.name !== "CanceledError") {
          setTopPages([]);
          setTopVideos([]);
        }
      }
    };

    fetchTopData();

    return () => controller.abort();
  }, [selectedYear]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${BASE_API_URL}/getUsers`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
          signal: controller.signal,
        });

        const map = {};
        response.data.forEach((user) => {
          map[user.enterpriseId] = user;
        });

        setUsersMap(map);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error(error);
        }
      }
    };

    fetchUsers();

    return () => controller.abort();
  }, []);

  const downloadFile = async () => {
    try {
      setIsDownloading(true);

      const response = await axios.get(
        `${BASE_API_URL}/downloadAnalytics/${selectedYear}`,
        {
          responseType: "blob",
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        },
      );

      const fileURL = window.URL.createObjectURL(response.data);

      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `AnalyticsData_${selectedYear}.xlsx`);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(fileURL);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };
  const fillMissingMonths = () => {
    const filledMonthView = [];
    for (let i = 1; i <= 12; i++) {
      const safeMonthView = Array.isArray(monthView) ? monthView : [];
      const monthData = safeMonthView
        .flatMap((x) => x?.Data || [])
        .filter((item) => item.month === i);
      const totalViews = monthData.reduce(
        (acc, cur) => acc + cur.total_views,
        0,
      );
      const totalViewsByEnterprise = monthData.map((userView) => {
        return {
          enterprise_id: userView.enterprise_id,
          total_views: userView.total_views,
        };
      });
      const uniqueView = monthData.reduce(
        (acc, cur) => acc + (cur.unique_views || 0),
        0,
      );
      //console.log(uniqueView, "uniqueView");
      if (monthData.length > 0) {
        filledMonthView.push({
          year: selectedYear,
          month: i,
          unique_views: uniqueView,
          total_views: totalViews,
          total_views_by_enterprise: totalViewsByEnterprise,
        });
      } else {
        filledMonthView.push({
          year: selectedYear,
          month: i,
          unique_views: 0,
          total_views: 0,
          total_views_by_enterprise: [],
        });
      }
    }
    return filledMonthView;
  };
  const handleBarClick = (event) => {
    const activeElement = event.chart.getElementsAtEventForMode(
      event,
      "nearest",
      { intersect: true },
      true,
    )[0];
    if (activeElement) {
      const { index } = activeElement;
      //  console.log("fill", fillMissingMonths()[index]);
      //  getDetailPerMonth(index + 1);
      setSelectedMonth(index + 1);
      setSelectedBarData(fillMissingMonths()[index]);
      setOpen(index);
    }
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        top: 40,
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#666",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#eee",
        },
        ticks: {
          color: "#666",
        },
        title: {
          display: true,
          text: "Views",
          color: "#666",
          font: {
            weight: "bold",
          },
        },
      },
    },

    plugins: {
      legend: {
        display: false,
        labels: {
          boxWidth: 12,
          filter: (item) => !item.text.includes("Ghost"),
        },
      },

      title: {
        display: false,
      },

      datalabels: {
        color: "#8e12db",
        anchor: "end",
        align: "top",
        rotation: -90,
        formatter: (value, context) => {
          if (context.dataset.label.includes("Ghost")) return "";
          return value === 0 ? "" : value;
        },
        font: {
          weight: "bold",
        },
      },
    },

    onClick: handleBarClick,
  };

  const labels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const filledData = fillMissingMonths();
  const currentMonth = new Date().getMonth() + 1;

  const maxTotal = Math.max(...filledData.map((d) => d.total_views), 1);
  const maxUnique = Math.max(...filledData.map((d) => d.unique_views), 1);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Ghost Unique",
        data: filledData.map((item) =>
          item.total_views === 0 && item.unique_views === 0 ? maxUnique * 2 : 0,
        ),
        backgroundColor: "rgba(156,163,175,0.15)",
        barThickness: 8,
        borderRadius: 6,
        order: 1,
        // categoryPercentage: 0.5,
        // barPercentage: 0.4,
      },

      {
        label: "Ghost Total",
        data: filledData.map((item) =>
          item.total_views === 0 && item.unique_views === 0
            ? maxTotal * 0.85
            : 0,
        ),
        backgroundColor: "rgba(194,163,255,0.15)",
        barThickness: 8,
        borderRadius: 6,
        order: 1,
        // categoryPercentage: 0.6,
        // barPercentage: 0.6,
      },

      {
        label: "Unique Views",
        data: filledData.map((item) => item.unique_views),
        backgroundColor: "#9CA3AF",
        barThickness: 8,
        borderRadius: 6,
        order: 2,
        // categoryPercentage: 0.5,
        // barPercentage: 0.4,
      },

      {
        label: "Total Views",
        data: filledData.map((item) => item.total_views),
        backgroundColor: "#C2A3FF",
        barThickness: 8,
        borderRadius: 6,
        order: 3,
        // categoryPercentage: 0.5,
        // barPercentage: 0.4,
      },
    ],
  };

  const handleCloseModal = () => {
    setOpen(null);
  };

  const topPagesChartData = {
    labels: topPages.map((item) => item.page),
    datasets: [
      {
        label: "Views",
        data: topPages.map((item) => item.views),
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  const topPagesOptions = {
    indexAxis: "y",
    responsive: true,
    scales: {
      x: {
        beginAtZero: true,
        grace: "20%",
      },
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Most Visited Pages",
        color: "#6e0089",
        font: { size: 18 },
      },
      datalabels: {
        color: "#6e0089",
        anchor: "end",
        align: "right",
        clamp: true,
        font: {
          weight: "bold",
        },
      },
    },
  };

  const topVideosChartData = {
    labels: topVideos.map((item) => item.video_title),
    datasets: [
      {
        label: "Views",
        data: topVideos.map((item) => item.views),
        backgroundColor: "rgba(53, 162, 235, 0.6)",
      },
    ],
  };

  const topVideosOptions = {
    indexAxis: "y",
    responsive: true,
    scales: {
      x: {
        beginAtZero: true,
        grace: "20%",
      },
    },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Top Ignite Talks",
        color: "#6e0089",
        font: { size: 18 },
      },
      datalabels: {
        color: "#6e0089",
        anchor: "end",
        align: "right",
        offset: 0,
        clamp: true,
        font: {
          weight: "bold",
        },
      },
    },
  };

  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const activeMonths = filledData
    .map((item, index) => ({
      ...item,
      monthIndex: index,
    }))
    .filter((item) => item.total_views > 0);

  let monthRange = "-";

  if (activeMonths.length === 1) {
    monthRange = monthNames[activeMonths[0].monthIndex];
  }

  if (activeMonths.length >= 2) {
    const last = activeMonths[activeMonths.length - 1].monthIndex;
    const secondLast = activeMonths[activeMonths.length - 2].monthIndex;

    monthRange = `${monthNames[secondLast]} - ${monthNames[last]}`;
  }

  const lastTwoMonths = activeMonths.slice(-2);

  const usersViewTotal = lastTwoMonths.reduce(
    (sum, item) => sum + item.unique_views,
    0,
  );

  const downloadTopPages = () => {
    const data = topPages.map((item) => ({
      Page: item.page,
      Views: item.views,
    }));

    downloadCSV(data, "TopPages");
  };

  const downloadTopVideos = () => {
    const data = topVideos.map((item) => ({
      Video: item.video_title,
      Views: item.views,
    }));

    downloadCSV(data, "TopVideos");
  };

  const downloadCSV = (data, fileName) => {
    const csvRows = [];

    const headers = Object.keys(data[0]);
    csvRows.push(headers.join(","));

    data.forEach((row) => {
      csvRows.push(headers.map((h) => row[h]).join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `${fileName}_${selectedYear}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <>
      <h2 className="analytics-heading">Analytics</h2>
      <div className="analytics-cards">
        <div className="analytics-card gradient">
          <div className="card-left">
            <p className="card-title">Most Visited Page</p>
            <h6>{topPages[0]?.page || "-"}</h6>

            <div className="card-footer">
              <img
                src={process.env.PUBLIC_URL + "/Assets/Analytics/eye.png"}
                alt="views"
                className="eye-img"
              />
              <span>{topPages[0]?.views || 0}</span>
            </div>
          </div>

          <div className="card-icon">
            <img
              src={process.env.PUBLIC_URL + "/Assets/Analytics/click.png"}
              alt="click"
            />
          </div>
        </div>

        <div className="analytics-card gradient">
          <div className="card-left">
            <p className="card-title">Top Ignite Talks</p>
            <h6>{topVideos[0]?.video_title || "-"}</h6>

            <div className="card-footer">
              <img
                src={process.env.PUBLIC_URL + "/Assets/Analytics/eye.png"}
                alt="views"
                className="eye-img"
              />
              <span>{topVideos[0]?.views || 0}</span>
            </div>
          </div>

          <div className="card-icon">
            <img
              src={process.env.PUBLIC_URL + "/Assets/Analytics/users.png"}
              alt="users"
            />
          </div>
        </div>

        <div className="analytics-card gradient">
          <div className="card-left">
            <p className="card-title">Users View</p>
            <h6>
              <h6>{monthRange}</h6>
            </h6>

            <div className="card-footer">
              <img
                src={process.env.PUBLIC_URL + "/Assets/Analytics/eye.png"}
                alt="views"
                className="eye-img"
              />
              <span>{usersViewTotal.toLocaleString()}</span>
            </div>
          </div>

          <div className="card-icon">
            <img
              src={process.env.PUBLIC_URL + "/Assets/Analytics/report.png"}
              alt="report"
            />
          </div>
        </div>
      </div>
      <div className="adminAnalytics-container">
        <div>
          <Modal
            open={open !== null}
            onClose={handleCloseModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            className="custom-modal"
          >
            <Box className="tableWrapper">
              <TableContainer component={Paper} className="tableContainer">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className="tableHeaderCell">
                        Enterprise ID
                      </TableCell>
                      <TableCell className="tableHeaderCell">
                        Capability
                      </TableCell>
                      <TableCell className="tableHeaderCell">
                        Industry
                      </TableCell>
                      <TableCell className="tableHeaderCell">
                        Platform
                      </TableCell>
                      <TableCell className="tableHeaderCell">Role</TableCell>
                      <TableCell className="tableHeaderCell">
                        Total Views
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthdata &&
                      monthdata
                        .filter(
                          (item) => !IgniteAdmins.includes(item.enterprise_id),
                        )
                        .map((item, index) => {
                          const user = usersMap[item.enterprise_id] || {};

                          return (
                            <TableRow
                              className="tableRow"
                              key={index}
                              sx={{
                                backgroundColor:
                                  index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                              }}
                            >
                              <TableCell className="tableCell">
                                {item.enterprise_id}
                              </TableCell>

                              <TableCell className="tableCell">
                                {user.capability || "-"}
                              </TableCell>

                              <TableCell className="tableCell">
                                {user.industry || "-"}
                              </TableCell>

                              <TableCell className="tableCell">
                                {user.category || "-"}
                              </TableCell>

                              <TableCell className="tableCell">
                                {user.role || "-"}
                              </TableCell>

                              <TableCell className="tableCell">
                                {item.total_count}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                sx={{
                  "& .MuiTablePagination-input": {
                    marginTop: "-10px",
                  },
                }}
                rowsPerPageOptions={[5, 10]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Modal>
        </div>
      </div>
      {/* <div id="date-picker-container">
        <Button
          onClick={downloadFile}
          style={{ height: "30px" }}
          size="small"
          variant="outlined"
          disabled={isDownloading ? true : false}
        >
          {isDownloading ? "Loading..." : "Download"}
        </Button>
        <DatePicker
          selected={new Date(selectedYear, 0, 1)}
          onChange={(date) => setSelectedYear(date.getFullYear())}
          dateFormat="yyyy"
          showYearPicker
          className="yearPicker"
        />
      </div> */}
      {analyticsError && (
        <Alert severity="error" className="errorAlert">
          <b>Internal Server Error:</b> Please try again with a hard refresh. If
          the issue persists, contact the LIS team for further assistance.
        </Alert>
      )}

      <div className="bottom-chart-box">
        <div className="chart-header">
          <h3>Unique Views and Total Views</h3>

          <div className="chart-actions">
            <div className="custom-legend">
              <span className="legend-item">
                <span className="legend-box grey"></span>
                Unique Views
              </span>

              <span className="legend-item">
                <span className="legend-box purple"></span>
                Total Views
              </span>
            </div>
            <div className="year-dropdown">
              <DatePicker
                selected={new Date(selectedYear, 0, 1)}
                onChange={(date) => setSelectedYear(date.getFullYear())}
                dateFormat="yyyy"
                showYearPicker
                className="mini-year-picker"
              />
              <span className="dropdown-arrow">▼</span>
            </div>
            <img
              src={process.env.PUBLIC_URL + "/Assets/Analytics/download.png"}
              alt="download"
              className="download-icon"
              onClick={downloadFile}
            />
          </div>
        </div>

        <div className="chart-body">
          <Bar plugins={[ChartDataLabels]} data={chartData} options={options} />
        </div>
      </div>

      <div className="top-charts-row">
        <div className="top-chart-box pink-bg">
          <div className="chart-header">
            <h3>Most Visited Pages</h3>

            <div className="chart-actions">
              <div className="year-dropdown">
                <DatePicker
                  selected={new Date(selectedYear, 0, 1)}
                  onChange={(date) => setSelectedYear(date.getFullYear())}
                  dateFormat="yyyy"
                  showYearPicker
                  className="mini-year-picker"
                />

                <span className="dropdown-arrow">▼</span>
              </div>
              <img
                src={process.env.PUBLIC_URL + "/Assets/Analytics/download.png"}
                alt="download"
                className="download-icon"
                onClick={downloadTopPages}
              />
            </div>
          </div>

          {topPages.map((item, index) => {
            const max = Math.max(...topPages.map((v) => v.views), 1);
            const percent = (item.views / max) * 100;

            return (
              <div key={index} className="progress-row thin">
                <span className="label">{item.page}</span>

                <div className="progress-line">
                  <div
                    className="progress-fill purple"
                    style={{ width: `${percent}%` }}
                  >
                    <span className="dot"></span>
                  </div>
                </div>

                <span className="value">{item.views}</span>
              </div>
            );
          })}
        </div>

        <div className="top-chart-box purple-bg">
          <div className="chart-header">
            <h3>Top Ignite Talks</h3>

            <div className="chart-actions">
              <div className="year-dropdown">
                <DatePicker
                  selected={new Date(selectedYear, 0, 1)}
                  onChange={(date) => setSelectedYear(date.getFullYear())}
                  dateFormat="yyyy"
                  showYearPicker
                  className="mini-year-picker"
                />

                <span className="dropdown-arrow">▼</span>
              </div>
              <img
                src={process.env.PUBLIC_URL + "/Assets/Analytics/download.png"}
                alt="download"
                className="download-icon"
                onClick={downloadTopVideos}
              />
            </div>
          </div>

          {topVideos.map((item, index) => {
            const max = Math.max(...topVideos.map((v) => v.views), 1);
            const percent = (item.views / max) * 100;

            return (
              <div key={index} className="progress-row thick">
                <span className="label">{item.video_title}</span>

                <div className="progress-bar thick">
                  <div
                    className="progress-fill blue"
                    style={{ width: `${percent}%` }}
                  ></div>
                </div>

                <span className="value">{item.views}</span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default AdminAnalyticsContent;
